/**
 * Auth page background — a floating island that rises in, trees that grow from the
 * valley outward and sway in a shared breeze, a freight truck driving a route between
 * two pins, drifting pollen and a few birds. Drawn procedurally on one <canvas>.
 *
 * Everything static (soil slab, mountains, each tree, grass) is painted once into
 * offscreen canvases with a seeded RNG, so a resize redraws the same island and each
 * frame is only a handful of drawImage calls.
 *
 * createAuthScene(canvas, { getAnchor }) → { destroy() }
 *   getAnchor(): DOMRect of the element the island should centre under (the hero).
 */

const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));
const hsl = (h, s, l, a = 1) => `hsla(${h},${s}%,${l}%,${a})`;

function mulberry(seed) {
  let a = seed;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function noise1D(r, n) {
  const v = Array.from({ length: n + 1 }, r);
  return (x) => {
    const f = clamp(x, 0, 0.9999) * n, i = Math.floor(f), t = f - i, s = t * t * (3 - 2 * t);
    return v[i] * (1 - s) + v[i + 1] * s;
  };
}

/* Spring from 0 → 1 with damping ratio z and natural frequency w (rad/s). */
function spring(t, w = 6, z = 0.8) {
  if (t <= 0) return 0;
  if (z >= 1) return 1 - (1 + w * t) * Math.exp(-w * t);
  const wd = w * Math.sqrt(1 - z * z);
  return 1 - Math.exp(-z * w * t) * (Math.cos(wd * t) + ((z * w) / wd) * Math.sin(wd * t));
}

function roundRectPath(g, x, y, w, h, r) {
  if (g.roundRect) { g.roundRect(x, y, w, h, r); return; }
  g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath();
}

export function createAuthScene(canvas, { getAnchor }) {
  const ctx = canvas.getContext('2d');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let W = 0, H = 0, DPR = 1, L = null, raf = 0, resizeTimer = 0, destroyed = false;
  let t0 = performance.now(), last = t0;

  function layer(w, h) {
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.ceil(w * DPR)); c.height = Math.max(1, Math.ceil(h * DPR));
    const g = c.getContext('2d'); g.scale(DPR, DPR);
    return [c, g];
  }

  function paintDots(g, dots) {
    dots.sort((a, b) => a.l - b.l);
    for (const d of dots) {
      g.fillStyle = hsl(d.h, d.s, d.l, d.a ?? 1);
      g.beginPath(); g.arc(d.x, d.y, d.r, 0, 6.2832); g.fill();
    }
  }

  /* ---------------- Tree painters ---------------- */
  function broadleaf(r, h, back) {
    const w = h * 0.95, [c, g] = layer(w, h);
    const bx = w / 2, lean = (r() - 0.5) * w * 0.08, trunkTop = h * 0.5, tw = Math.max(1.6, h * 0.028);
    const bark = g.createLinearGradient(bx - tw, 0, bx + tw, 0);
    bark.addColorStop(0, '#3b2e22'); bark.addColorStop(0.6, '#5a4634'); bark.addColorStop(1, '#3a2c20');
    g.fillStyle = bark; g.beginPath(); g.moveTo(bx - tw, h);
    g.quadraticCurveTo(bx - tw * 0.5 + lean * 0.4, h * 0.75, bx + lean - tw * 0.35, trunkTop);
    g.lineTo(bx + lean + tw * 0.35, trunkTop);
    g.quadraticCurveTo(bx + tw * 0.5 + lean * 0.4, h * 0.75, bx + tw, h); g.fill();
    const cx = bx + lean, cy = h * 0.33, rx = w * 0.34, ry = h * 0.25;
    const blobs = [{ x: cx, y: cy - ry * 0.45, r: h * (0.12 + r() * 0.04) }];
    const k = 4 + Math.floor(r() * 4);
    for (let i = 0; i < k; i++) {
      const a = (i / k) * 6.283 + r() * 0.6;
      blobs.push({ x: cx + Math.cos(a) * rx * (0.55 + r() * 0.35), y: cy + Math.sin(a) * ry * (0.5 + r() * 0.35), r: h * (0.085 + r() * 0.06) });
    }
    g.strokeStyle = '#3d3024'; g.lineCap = 'round';
    for (const b of blobs) {
      g.lineWidth = tw * 0.55; g.beginPath(); g.moveTo(cx, trunkTop + h * 0.02);
      g.quadraticCurveTo((cx + b.x) / 2, (trunkTop + b.y) / 2 + h * 0.04, b.x, b.y); g.stroke();
    }
    const baseH = 92 + r() * 30, baseS = back ? 26 : 40, baseL = back ? 42 : 30, dr = Math.max(1.1, h * 0.0105);
    const dots = [];
    for (const b of blobs) {
      g.fillStyle = hsl(baseH, baseS, baseL - 10); g.beginPath(); g.arc(b.x, b.y, b.r * 0.82, 0, 6.283); g.fill();
      const n = Math.floor(((b.r * b.r) / (dr * dr)) * 1.15);
      for (let i = 0; i < n; i++) {
        const rr = Math.sqrt(r()) * b.r, a = r() * 6.283, x = b.x + Math.cos(a) * rr, y = b.y + Math.sin(a) * rr;
        const lit = (Math.cos(a) * 0.5 - Math.sin(a) * 0.85) * (rr / b.r);
        const top = 1 - y / (h * 0.6);
        dots.push({ x, y, r: dr * (0.7 + r() * 0.8), h: baseH + (r() - 0.5) * 16, s: baseS + (r() - 0.5) * 10, l: baseL + lit * 12 + top * 8 + (r() - 0.5) * 9 });
      }
    }
    paintDots(g, dots);
    return { c, w, h };
  }

  function pine(r, h, back) {
    const w = h * 0.5, [c, g] = layer(w, h), bx = w / 2;
    g.strokeStyle = '#3b2d21'; g.lineWidth = Math.max(1.2, h * 0.016); g.lineCap = 'round';
    g.beginPath(); g.moveTo(bx, h); g.lineTo(bx, h * 0.04); g.stroke();
    const tiers = 9 + Math.floor(r() * 4), dr = Math.max(0.9, h * 0.0085);
    const baseH = 138 + r() * 14, baseS = back ? 18 : 30, baseL = back ? 36 : 22;
    const dots = [];
    for (let i = 0; i < tiers; i++) {
      const t = i / (tiers - 1), ty = h * 0.88 - t * h * 0.84, half = w * 0.46 * Math.pow(1 - t, 0.9) + w * 0.05;
      for (const side of [-1, 1]) {
        const m = Math.ceil(half / (dr * 0.8));
        for (let j = 0; j <= m; j++) {
          const f = j / m, x = bx + side * half * f, y = ty + f * f * half * 0.42;
          for (let q = 0; q < 3; q++) {
            const oy = (r() - 0.5) * dr * 5;
            dots.push({ x: x + (r() - 0.5) * dr * 2, y: y + oy, r: dr * (0.6 + r() * 0.7), h: baseH + (r() - 0.5) * 10, s: baseS, l: baseL + (oy < 0 ? 7 : -3) + (side > 0 ? 3 : 0) + t * 6 + (r() - 0.5) * 7 });
          }
        }
      }
    }
    paintDots(g, dots);
    return { c, w, h };
  }

  function cypress(r, h, back) {
    const w = h * 0.36, [c, g] = layer(w, h), bx = w / 2, dr = Math.max(0.9, h * 0.009);
    g.strokeStyle = '#3b2d21'; g.lineWidth = Math.max(1.2, h * 0.02);
    g.beginPath(); g.moveTo(bx, h); g.lineTo(bx, h * 0.82); g.stroke();
    const baseH = 110 + r() * 18, baseS = back ? 22 : 34, baseL = back ? 40 : 27, dots = [];
    const n = Math.floor(((h * w) / (dr * dr)) * 0.5);
    for (let i = 0; i < n; i++) {
      const y = h * 0.04 + r() * h * 0.82, t = (y - h * 0.04) / (h * 0.82);
      const prof = Math.sin(Math.pow(t, 0.75) * Math.PI) * 0.95 + 0.05;
      const half = w * 0.46 * prof * (t > 0.85 ? 1 - ((t - 0.85) / 0.15) * 0.6 : 1), x = bx + (r() * 2 - 1) * half;
      const lit = (x - bx) / (half + 1);
      dots.push({ x, y, r: dr * (0.7 + r() * 0.7), h: baseH + (r() - 0.5) * 12, s: baseS, l: baseL + lit * 8 + (1 - t) * 6 + (r() - 0.5) * 8 });
    }
    paintDots(g, dots);
    return { c, w, h };
  }

  function newParticle(r, initial, cx, IW, surface) {
    return {
      x: cx + (r() - 0.5) * IW * 0.95, y: initial ? surface - r() * H * 0.55 : surface - r() * 30,
      vx: (r() - 0.5) * 8, vy: -(6 + r() * 14), r: 1 + r() * 2.4, leaf: r() < 0.35, rot: r() * 6.28,
      vr: (r() - 0.5) * 1.2, life: 0, max: 7 + r() * 9, teal: r() < 0.5,
    };
  }

  /* ---------------- Build all static layers ---------------- */
  function build() {
    DPR = Math.min(2, window.devicePixelRatio || 1);
    W = window.innerWidth; H = window.innerHeight;
    canvas.width = Math.round(W * DPR); canvas.height = Math.round(H * DPR);
    const desktop = W >= 1024;
    const hero = getAnchor();
    const cx = desktop && hero ? hero.left + hero.width / 2 : W / 2;
    const IW = desktop && hero ? Math.min(hero.width * 1.08, 1300) : Math.min(W * 1.25, 900);
    const T = IW * 0.072;                                 // slab thickness
    const surface = H - T * 1.85 - (desktop ? 10 : 4);   // y of the island's top surface
    const s = IW / 1250;                                  // detail scale
    const r = mulberry(20261103);

    /* soil slab */
    const N = 120, topN = noise1D(r, 18), botN = noise1D(r, 40), top = [], bot = [];
    const endDropAt = (u) => Math.pow(clamp((Math.abs(u) - 0.86) / 0.14), 2) * T * 0.55;
    for (let i = 0; i <= N; i++) {
      const u = (i / N) * 2 - 1, x = ((u + 1) / 2) * IW, a = Math.abs(u), endDrop = endDropAt(u);
      top.push([x, (topN(i / N) - 0.5) * 5 * s + endDrop]);
      const thick = T * (0.28 + 0.72 * Math.pow(1 - u * u, 0.55)) * (0.82 + botN(i / N) * 0.36);
      const spike = r() < 0.18 ? r() * T * 0.45 * (1 - a) : 0;
      bot.push([x, Math.max(endDrop + 4, thick + spike)]);
    }
    const M = 34 * s + 10, slabH = T * 2 + M;
    const [slab, sg] = layer(IW, slabH);
    sg.translate(0, M);
    const path = new Path2D();
    path.moveTo(top[0][0], top[0][1]);
    for (let i = 1; i <= N; i++) { const p = top[i - 1], q = top[i]; path.quadraticCurveTo(p[0], p[1], (p[0] + q[0]) / 2, (p[1] + q[1]) / 2); }
    path.lineTo(top[N][0], top[N][1]);
    for (let i = N; i >= 1; i--) { const p = bot[i], q = bot[i - 1]; path.quadraticCurveTo(p[0], p[1], (p[0] + q[0]) / 2, (p[1] + q[1]) / 2); }
    path.closePath();
    const soil = sg.createLinearGradient(0, 0, 0, T * 1.3);
    soil.addColorStop(0, '#6e5a43'); soil.addColorStop(0.35, '#4a3a2b'); soil.addColorStop(1, '#2a2119');
    sg.fillStyle = soil; sg.fill(path);
    sg.save(); sg.clip(path);
    for (let i = 0; i < 260; i++) {           // bark / strata streaks
      const y = r() * T * 1.3, x = r() * IW, len = IW * (0.02 + r() * 0.12), dark = r() < 0.6;
      sg.strokeStyle = dark ? `rgba(28,20,13,${0.15 + r() * 0.25})` : `rgba(170,140,100,${0.08 + r() * 0.15})`;
      sg.lineWidth = (0.6 + r() * 1.6) * s; sg.beginPath(); sg.moveTo(x, y);
      sg.bezierCurveTo(x + len * 0.3, y + (r() - 0.5) * 4 * s, x + len * 0.6, y + (r() - 0.5) * 4 * s, x + len, y + (r() - 0.5) * 3 * s);
      sg.stroke();
    }
    const moss = [];
    for (let i = 0; i < IW * 3.2; i++) {     // moss along the top lip
      const x = r() * IW, u = (x / IW) * 2 - 1;
      const y = (topN(x / IW) - 0.5) * 5 * s + endDropAt(u) + Math.pow(r(), 2.2) * T * 0.32;
      moss.push({ x, y, r: (1 + r() * 1.6) * s, h: 85 + r() * 40, s: 35 + r() * 15, l: 24 + r() * 20, a: 0.9 });
    }
    for (let p = 0; p < 12; p++) {           // moss patches on the face
      const px = r() * IW, py = T * (0.2 + r() * 0.6), pr = T * (0.08 + r() * 0.14);
      for (let i = 0; i < (pr * pr * 0.35) / (s * s); i++) {
        const a = r() * 6.283, d = Math.sqrt(r()) * pr;
        moss.push({ x: px + Math.cos(a) * d * 1.8, y: py + Math.sin(a) * d * 0.6, r: (0.8 + r() * 1.3) * s, h: 80 + r() * 40, s: 30 + r() * 15, l: 24 + r() * 18, a: 0.85 });
      }
    }
    paintDots(sg, moss);
    sg.restore();
    sg.lineCap = 'round';
    for (let i = 0; i < 70; i++) {           // hanging roots
      const idx = 4 + Math.floor(r() * (N - 8)), [x, y] = bot[idx];
      const len = T * (0.25 + r() * 0.9) * (1 - Math.abs((idx / N) * 2 - 1) * 0.6);
      sg.strokeStyle = `rgba(${60 + r() * 30},${45 + r() * 20},${30 + r() * 15},${0.55 + r() * 0.35})`;
      sg.lineWidth = (0.5 + r() * 1.5) * s;
      sg.beginPath(); sg.moveTo(x, y - 2);
      sg.bezierCurveTo(x + (r() - 0.5) * 18 * s, y + len * 0.4, x + (r() - 0.5) * 26 * s, y + len * 0.7, x + (r() - 0.5) * 22 * s, y + len);
      sg.stroke();
    }
    for (let i = 0; i < 40; i++) {           // loose crumbs
      const [x, y] = bot[Math.floor(r() * N)];
      sg.fillStyle = `rgba(55,42,30,${0.4 + r() * 0.4})`;
      sg.beginPath(); sg.arc(x + (r() - 0.5) * 10, y + T * (0.15 + r() * 0.6), (1 + r() * 2.2) * s, 0, 6.283); sg.fill();
    }

    /* misty mountains in the valley */
    const peakMax = IW * 0.17, mtH = peakMax + 12, [mt, mg] = layer(IW, mtH);
    const ranges = [
      { a: 0.3, c: [150, 162, 156], n: 9, hs: 1 },
      { a: 0.42, c: [124, 140, 132], n: 8, hs: 0.8 },
      { a: 0.55, c: [100, 118, 110], n: 7, hs: 0.6 },
    ];
    for (const R of ranges) {
      for (let i = 0; i < R.n; i++) {
        const u = (r() * 2 - 1) * 0.58, x = ((u + 1) / 2) * IW;
        const ph = peakMax * R.hs * (0.45 + r() * 0.55) * (1 - Math.abs(u) * 0.5), pw = ph * (0.28 + r() * 0.2);
        const gr = mg.createLinearGradient(0, mtH - ph, 0, mtH);
        gr.addColorStop(0, `rgba(${R.c},${R.a})`); gr.addColorStop(0.7, `rgba(${R.c},${R.a * 0.35})`); gr.addColorStop(1, `rgba(${R.c},0)`);
        mg.fillStyle = gr; mg.beginPath(); mg.moveTo(x - pw, mtH);
        mg.bezierCurveTo(x - pw * 0.45, mtH - ph * 0.45, x - pw * 0.3, mtH - ph, x, mtH - ph);
        mg.bezierCurveTo(x + pw * 0.3, mtH - ph, x + pw * 0.45, mtH - ph * 0.45, x + pw, mtH); mg.fill();
      }
    }
    // fade the range into mist by masking alpha (no painted rectangle edges)
    mg.globalCompositeOperation = 'destination-in';
    const mist = mg.createLinearGradient(0, 0, 0, mtH);
    mist.addColorStop(0, 'rgba(0,0,0,1)'); mist.addColorStop(0.6, 'rgba(0,0,0,.85)'); mist.addColorStop(1, 'rgba(0,0,0,.05)');
    mg.fillStyle = mist; mg.fillRect(0, 0, IW, mtH);
    const side = mg.createLinearGradient(0, 0, IW, 0);
    side.addColorStop(0, 'rgba(0,0,0,0)'); side.addColorStop(0.18, 'rgba(0,0,0,1)'); side.addColorStop(0.82, 'rgba(0,0,0,1)'); side.addColorStop(1, 'rgba(0,0,0,0)');
    mg.fillStyle = side; mg.fillRect(0, 0, IW, mtH);
    mg.globalCompositeOperation = 'source-over';

    /* trees — short in the valley, tall at the ends, like a framed clearing */
    const trees = [], count = desktop ? 30 : 18, plan = [];
    for (let i = 0; i < count; i++) plan.push(-0.86 + (1.72 * (i + 0.2 + r() * 0.6)) / count);
    plan.push(-0.74, 0.8); // two hero trees at the ends
    plan.forEach((u, idx) => {
      const a = Math.abs(u), heroTree = idx >= count, back = !heroTree && r() < 0.38;
      let type = a > 0.5 ? (r() < 0.55 ? 'broad' : 'pine') : r() < 0.5 ? 'pine' : r() < 0.6 ? 'cypress' : 'broad';
      let h = IW * (0.06 + 0.2 * Math.pow(a, 1.35)) * (0.75 + r() * 0.5);
      if (heroTree) { type = idx === count ? 'pine' : 'broad'; h = IW * (idx === count ? 0.27 : 0.3); }
      if (type === 'broad') h *= 1.05;
      if (back) h *= 0.82;
      h = Math.max(28, h);
      const painted = type === 'broad' ? broadleaf(r, h, back) : type === 'pine' ? pine(r, h, back) : cypress(r, h, back);
      trees.push({ ...painted, x: ((u + 1) / 2) * IW, y: back ? -3 * s : 2 * s, back, delay: 0.15 + a * 0.9 + r() * 0.3, phase: r() * 6.283, freq: 0.6 + r() * 0.5 });
    });
    trees.sort((p, q) => (p.back === q.back ? q.h - p.h : p.back ? -1 : 1));

    /* foreground: bushes, grass, ferns, the freight lane */
    const roadY = 6 * s, FH = 46 * s + 20, FY = 30 * s + 10;
    const [front, fg] = layer(IW, FH); fg.translate(0, FY);
    const rx0 = ((-0.66 + 1) / 2) * IW, rx1 = ((0.64 + 1) / 2) * IW;
    const fDots = [];
    for (let i = 0; i < 26; i++) {
      const x = r() * IW, u = (x / IW) * 2 - 1;
      if (Math.abs(u) > 0.9) continue;
      const br = (8 + r() * 16) * s;
      for (let k = 0; k < (br * br * 0.5) / (s * s); k++) {
        const a = r() * Math.PI + Math.PI, d = Math.sqrt(r()) * br;
        fDots.push({ x: x + Math.cos(a) * d * 1.3, y: Math.sin(a) * d + 2 * s, r: (1 + r() * 1.4) * s, h: 85 + r() * 40, s: 35 + r() * 12, l: 24 + -Math.sin(a) * 12 + r() * 10 });
      }
    }
    paintDots(fg, fDots);
    fg.lineCap = 'round';
    for (let i = 0; i < IW / 1.6; i++) {
      const x = r() * IW, a = Math.abs((x / IW) * 2 - 1);
      if (a > 0.93) continue;
      const bh = (4 + r() * 13) * s * (1 - a * 0.4), lean = (r() - 0.5) * 8 * s, y0 = (r() * 4 - 1) * s;
      fg.strokeStyle = hsl(88 + r() * 40, 38 + r() * 18, 26 + r() * 22); fg.lineWidth = (0.7 + r() * 0.9) * s;
      fg.beginPath(); fg.moveTo(x, y0); fg.quadraticCurveTo(x + lean * 0.3, y0 - bh * 0.6, x + lean, y0 - bh); fg.stroke();
    }
    for (let i = 0; i < 14; i++) {
      const x = r() * IW;
      if (Math.abs((x / IW) * 2 - 1) > 0.88) continue;
      const fronds = 5 + Math.floor(r() * 4), fl = (14 + r() * 14) * s;
      for (let f = 0; f < fronds; f++) {
        const ang = -Math.PI / 2 + (f / (fronds - 1) - 0.5) * 2.2, ex = x + Math.cos(ang) * fl, ey = Math.sin(ang) * fl * 0.8;
        fg.strokeStyle = hsl(105 + r() * 20, 40, 30 + r() * 12); fg.lineWidth = 0.9 * s;
        fg.beginPath(); fg.moveTo(x, 2 * s); fg.quadraticCurveTo((x + ex) / 2, ey * 0.9, ex, ey + fl * 0.25); fg.stroke();
        for (let l = 1; l < 7; l++) {
          const t = l / 7, lx = x + (ex - x) * t, ly = ey * t * 0.95 + fl * 0.25 * t * t;
          fg.beginPath(); fg.moveTo(lx, ly); fg.lineTo(lx + 3 * s * (1 - t), ly - 3 * s * (1 - t));
          fg.moveTo(lx, ly); fg.lineTo(lx - 3 * s * (1 - t), ly - 2 * s * (1 - t)); fg.stroke();
        }
      }
    }
    fg.fillStyle = 'rgba(206,188,152,.95)'; fg.beginPath(); roundRectPath(fg, rx0, roadY - 2.6 * s, rx1 - rx0, 5.2 * s, 3 * s); fg.fill();
    fg.fillStyle = 'rgba(120,100,70,.35)'; fg.fillRect(rx0, roadY + 2 * s, rx1 - rx0, 0.8 * s);
    fg.strokeStyle = 'rgba(14,138,125,.55)'; fg.lineWidth = 1.1 * s; fg.setLineDash([5 * s, 5 * s]);
    fg.beginPath(); fg.moveTo(rx0 + 6 * s, roadY); fg.lineTo(rx1 - 6 * s, roadY); fg.stroke(); fg.setLineDash([]);

    // tufts in front of the lane so the truck passes "through" the grass
    const [tuft, tg] = layer(IW, FH); tg.translate(0, FY); tg.lineCap = 'round';
    for (let i = 0; i < IW / 9; i++) {
      const x = r() * IW;
      if (Math.abs((x / IW) * 2 - 1) > 0.9) continue;
      const bh = (3 + r() * 7) * s, lean = (r() - 0.5) * 6 * s, y0 = roadY + 3.4 * s + r() * 2 * s;
      tg.strokeStyle = hsl(90 + r() * 35, 40 + r() * 12, 28 + r() * 18); tg.lineWidth = (0.7 + r() * 0.8) * s;
      tg.beginPath(); tg.moveTo(x, y0); tg.quadraticCurveTo(x + lean * 0.3, y0 - bh * 0.6, x + lean, y0 - bh); tg.stroke();
    }

    const pr = mulberry(77);
    const particles = Array.from({ length: desktop ? 46 : 26 }, () => newParticle(pr, true, cx, IW, surface));
    const birds = Array.from({ length: desktop ? 6 : 3 }, () => ({ x: pr() * W, y: H * (0.12 + pr() * 0.3), v: 18 + pr() * 22, s: 3.5 + pr() * 3, f: 5 + pr() * 3, p: pr() * 6.28 }));

    L = { cx, IW, surface, s, M, slab, slabH, mt, mtH, trees, front, tuft, FH, FY, roadY, rx0, rx1, particles, birds, pr };
  }

  function drawPin(x, y, s) {
    const h = 19 * s, r = 6.4 * s;
    ctx.save(); ctx.translate(x, y - 1 * s);
    ctx.fillStyle = 'rgba(0,0,0,.12)'; ctx.beginPath(); ctx.ellipse(0, 0, r * 0.9, r * 0.3, 0, 0, 6.283); ctx.fill();
    ctx.fillStyle = '#0e8a7d'; ctx.beginPath(); ctx.moveTo(0, 0);
    ctx.bezierCurveTo(-r * 0.4, -h * 0.45, -r, -h * 0.55, -r, -h + r);
    ctx.arc(0, -h + r, r, Math.PI, 0);
    ctx.bezierCurveTo(r, -h * 0.55, r * 0.4, -h * 0.45, 0, 0); ctx.fill();
    ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(0, -h + r, r * 0.42, 0, 6.283); ctx.fill();
    ctx.restore();
  }

  function drawTruck(x, y, s, t) {
    const k = s * 1.45, bounce = reduceMotion ? 0 : Math.sin(t * 18) * 0.25 * k;
    ctx.save(); ctx.translate(x, y + bounce);
    ctx.fillStyle = 'rgba(0,0,0,.14)'; ctx.beginPath(); ctx.ellipse(-2 * k, 3.6 * k, 20 * k, 2 * k, 0, 0, 6.283); ctx.fill();
    ctx.fillStyle = '#ffffff'; ctx.strokeStyle = 'rgba(22,53,44,.25)'; ctx.lineWidth = 0.8 * k;
    ctx.beginPath(); roundRectPath(ctx, -22 * k, -14 * k, 28 * k, 14 * k, 2 * k); ctx.fill(); ctx.stroke();
    ctx.fillStyle = '#0e8a7d'; ctx.fillRect(-22 * k, -4 * k, 28 * k, 1.6 * k);
    ctx.fillStyle = '#2f8f57'; ctx.beginPath(); ctx.ellipse(-8 * k, -9 * k, 3.4 * k, 1.8 * k, -0.6, 0, 6.283); ctx.fill();
    ctx.fillStyle = '#0e8a7d'; ctx.beginPath(); ctx.moveTo(7 * k, 0); ctx.lineTo(7 * k, -11 * k); ctx.lineTo(13 * k, -11 * k);
    ctx.lineTo(17 * k, -6 * k); ctx.lineTo(17 * k, 0); ctx.closePath(); ctx.fill();
    ctx.fillStyle = 'rgba(220,245,240,.95)'; ctx.beginPath(); ctx.moveTo(9.5 * k, -9.5 * k); ctx.lineTo(12.6 * k, -9.5 * k);
    ctx.lineTo(15.4 * k, -6 * k); ctx.lineTo(9.5 * k, -6 * k); ctx.closePath(); ctx.fill();
    for (const [col, rad] of [['#1d2723', 2.6], ['#9aa7a2', 1]]) {
      ctx.fillStyle = col;
      for (const wx of [-17, -9, 13]) { ctx.beginPath(); ctx.arc(wx * k, 0.6 * k, rad * k, 0, 6.283); ctx.fill(); }
    }
    ctx.restore();
  }

  /* ---------------- Render one frame ---------------- */
  function render(now, still = false) {
    if (!L) return;
    const scrollY = window.scrollY || 0;
    const t = still ? 99 : (now - t0) / 1000;
    const dt = still ? 0 : Math.min(0.05, (now - last) / 1000);
    last = now;
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0); ctx.clearRect(0, 0, W, H);
    const { cx, IW, surface, s } = L;

    // birds — far layer, barely moves with scroll
    ctx.strokeStyle = 'rgba(38,54,46,.5)'; ctx.lineCap = 'round'; ctx.lineWidth = 1.2;
    for (const b of L.birds) {
      if (!still) { b.x += b.v * dt; if (b.x > W + 30) { b.x = -30; b.y = H * (0.12 + L.pr() * 0.3); } }
      const flap = Math.sin(t * b.f + b.p) * b.s * 0.7, y = b.y - scrollY * 0.04;
      ctx.beginPath(); ctx.moveTo(b.x - b.s, y - flap);
      ctx.quadraticCurveTo(b.x - b.s * 0.4, y - flap * 0.3, b.x, y);
      ctx.quadraticCurveTo(b.x + b.s * 0.4, y - flap * 0.3, b.x + b.s, y - flap); ctx.stroke();
    }

    // island group: intro rise (critically damped), gentle float, scroll parallax
    const intro = still ? 1 : spring(t - 0.05, 4.2, 1);
    const rise = Math.min(scrollY * 0.1, H * 0.1);
    const bob = reduceMotion ? 0 : Math.sin(t * 0.72) * 3 * s;
    const ox = cx - IW / 2, oy = surface - rise + bob + (1 - intro) * 70;
    const alpha = clamp(intro * 1.15);
    ctx.globalAlpha = alpha;

    const glow = ctx.createRadialGradient(cx, oy - IW * 0.1, 10, cx, oy - IW * 0.1, IW * 0.5);
    glow.addColorStop(0, 'rgba(255,255,255,.75)'); glow.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = glow; ctx.fillRect(cx - IW * 0.6, oy - IW * 0.6, IW * 1.2, IW * 1.1);

    ctx.globalAlpha = alpha * (still ? 1 : clamp((t - 0.2) / 1.4));
    ctx.drawImage(L.mt, ox, oy - L.mtH + 4 * s + rise * 0.25, IW, L.mtH);   // lags the island: depth
    ctx.globalAlpha = alpha;

    // trees grow from the ground (slight organic overshoot) and bend in a shared breeze
    const wind = reduceMotion ? 0 : Math.sin(t * 0.45) * 0.6 + Math.sin(t * 1.1 + 1.3) * 0.4;
    for (const tr of L.trees) {
      const g = still || reduceMotion ? 1 : spring(t - tr.delay, 6, 0.78);
      if (g <= 0.001) continue;
      const sway = reduceMotion ? 0 : (Math.sin(t * tr.freq + tr.phase) * 0.012 + wind * 0.01) * Math.min(1, tr.h / 200);
      ctx.save();
      ctx.translate(ox + tr.x, oy + tr.y);
      ctx.transform(1, 0, -sway, 1, 0, 0); // base fixed, crown moves
      ctx.scale(Math.pow(g, 0.85), g);
      ctx.drawImage(tr.c, -tr.w / 2, -tr.h, tr.w, tr.h);
      ctx.restore();
    }

    ctx.drawImage(L.slab, ox, oy - L.M, IW, L.slabH);
    ctx.drawImage(L.front, ox, oy - L.FY, IW, L.FH);

    // freight route: two pins, a truck easing between them on a 16s loop
    const pinA = ox + L.rx0 + 4 * s, pinB = ox + L.rx1 - 4 * s, ry = oy + L.roadY;
    const cycle = 16, ph = still ? 0.42 : ((((t - 1.6) % cycle) + cycle) % cycle) / cycle;
    const move = clamp((ph - 0.06) / 0.82);
    const ease = move < 0.5 ? 2 * move * move : 1 - Math.pow(-2 * move + 2, 2) / 2;
    const truckA = t < 1.6 && !still ? 0 : clamp(Math.min(ph / 0.05, (1 - ph) / 0.05));
    drawPin(pinA, ry, s); drawPin(pinB, ry, s);
    if (truckA > 0) {
      ctx.globalAlpha = clamp(intro) * truckA;
      drawTruck(pinA + (pinB - pinA) * ease, ry, s, t);
      ctx.globalAlpha = alpha;
    }
    ctx.drawImage(L.tuft, ox, oy - L.FY, IW, L.FH);
    ctx.globalAlpha = 1;

    // drifting pollen and leaves
    for (let i = 0; i < L.particles.length; i++) {
      const p = L.particles[i];
      if (!still) {
        p.life += dt; p.x += (p.vx + Math.sin(t * 0.8 + i) * 4) * dt; p.y += p.vy * dt; p.rot += p.vr * dt;
        if (p.life > p.max || p.y < H * 0.08) L.particles[i] = newParticle(L.pr, false, cx, IW, surface);
      }
      const fade = still ? 0.5 : Math.min(1, p.life / 1.2, (p.max - p.life) / 1.5) * clamp((t - 1) / 1.5);
      if (fade <= 0) continue;
      ctx.globalAlpha = fade * 0.55;
      ctx.fillStyle = p.teal ? 'rgb(20,160,145)' : 'rgb(110,160,70)';
      const py = p.y - rise;
      if (p.leaf) {
        ctx.save(); ctx.translate(p.x, py); ctx.rotate(p.rot);
        ctx.beginPath(); ctx.ellipse(0, 0, p.r * 1.8, p.r * 0.8, 0, 0, 6.283); ctx.fill(); ctx.restore();
      } else {
        ctx.beginPath(); ctx.arc(p.x, py, p.r * 0.8, 0, 6.283); ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }

  function loop(now) {
    if (destroyed) return;
    render(now);
    raf = requestAnimationFrame(loop);
  }

  const onResize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => { build(); if (reduceMotion) render(performance.now(), true); }, 120);
  };
  let scrollQueued = false;
  const onScrollStill = () => {
    if (scrollQueued) return; scrollQueued = true;
    requestAnimationFrame(() => { scrollQueued = false; render(performance.now(), true); });
  };

  build();
  t0 = performance.now(); last = t0;
  window.addEventListener('resize', onResize);
  if (reduceMotion) {
    render(t0, true);
    window.addEventListener('scroll', onScrollStill, { passive: true });
  } else {
    raf = requestAnimationFrame(loop);
  }

  return {
    rebuild: () => { build(); if (reduceMotion) render(performance.now(), true); },
    destroy() {
      destroyed = true;
      cancelAnimationFrame(raf);
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScrollStill);
    },
  };
}
